import { DateRangePicker, MonthPicker, Toggle } from '@/components_deprecated'
import { useMassActivityExportForm } from '@/modules/activities/hooks'
import { responsiveExportGridAreas } from '@/modules/companies/util'
import { ExportFormat, ExportOption } from '@/modules/exports'
import { EXPORT_UNKNOWN_LOCATIONS } from '@/modules/exports/mutations'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { useOnErrorAuto, useUser } from '@/utils/hooks'
import { useMutation } from '@apollo/client'
import { ComponentSwitch, Icon, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import type { FormButton } from '../common'
import { Field, Form, Section } from '../common'

export const ExportUnknownDriverLocations: React.FC = () => {
  const me = useUser()
  const translations = useTranslate({
    unknownDriverAndDrivingWithoutCard:
      'activities.unknown-driver-and-driving-without-card',
    exportWeekly: 'activities.export-monthly-weekly',
    activities: 'common.activities',
    exportAll: 'settings.export-all-driver-activities',
    period: 'common.period',
    exportType: 'common.type',
    exportFormat: 'common.format',
    exportMethod: 'common.export-method',

    export: 'common.export',
    orderBy: 'common.order-by',
    selectUsers: 'common.select-users',
    selectAll: 'common.select-all',
    includeAbsences: 'settings.include-absences',

    downloadOnlySupportsPdf: 'settings.download-only-supports-pdf',

    lastName: 'common.lastName',
    userType: 'common.user-type',
    externalId: 'common.external-id',
    error: 'error.generic-server-error',
    downloadSuccess: 'exports.success-user-exports',
  })

  const { loading, form, updateForm, handler } = useMassActivityExportForm()

  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()

  const [doExport] = useMutation(EXPORT_UNKNOWN_LOCATIONS, {
    onCompleted(data) {
      if (data?.exportUnknownLocations.ok) {
        addToast('success', translations.downloadSuccess)
      } else addToast('error', translations.error)
    },
    onError: onErrorAuto(translations.error),
  })

  const areas = !isMobile
    ? `
        ${responsiveExportGridAreas(true)}
      `
    : `
        ${responsiveExportGridAreas(true)}
      `

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading,
        },
        onClick: () => {
          void doExport({
            variables: {
              user: me.id,
              dateStart: formatISO(form.dateStart),
              dateEnd: formatISO(form.dateEnd),
            },
          })
        },
      },
    ],
    [
      doExport,
      form.dateEnd,
      form.dateStart,
      loading,
      me.id,
      translations.export,
    ]
  )

  useEffect(() => {
    if (
      form.exportOption === ExportOption.DOWNLOAD &&
      form.exportFormat !== ExportFormat.PDF
    ) {
      updateForm({ exportFormat: ExportFormat.PDF })
    }
  }, [form.exportOption, form.exportFormat, updateForm, form.exportType])

  return (
    <Section
      header={translations.unknownDriverAndDrivingWithoutCard}
      loading={loading}
      actions={
        <Toggle
          value={form.useDateRange}
          thumbBackground="secondary"
          iconMode="svg"
          localOnIconProps={{
            icon: 'calendar-selection',
            fill: form.useDateRange ? 'white' : 'primary',
          }}
          localOffIconProps={{
            icon: 'calendar-month',
            fill: !form.useDateRange ? 'white' : 'primary',
          }}
          onChange={() => handler('useDateRange')(!form.useDateRange)}
        />
      }
    >
      <Form
        grid={{
          columns: !isMobile ? '1fr 2fr 2fr' : '1fr',
          areas: areas,
        }}
        buttons={formButtons}
      >
        <Field area="month">
          <label>{translations.period}</label>

          <ComponentSwitch>
            <ComponentSwitch.Case case={form.useDateRange}>
              <DateRangePicker
                value={[form.dateStart, form.dateEnd]}
                grayed={false}
                onChange={handleDateRangeChange(updateForm)}
              />
            </ComponentSwitch.Case>

            <ComponentSwitch.Case default>
              <MonthPicker
                value={form.month}
                light
                futureYears={0}
                noYearCycle
                onChange={handler('month')}
              />
            </ComponentSwitch.Case>
          </ComponentSwitch>
        </Field>
      </Form>
    </Section>
  )
}
